import { faTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Flex, Label, Text, useOpenModal } from '@modusign/design-system';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { WindowModal } from '@app/components/Modal/WindowModal';
import LabelApplyModalPopover from '@app/features/label/components/LabelPopovers/LabelApplyPopover';
import useGetLabelListByIds from '@app/features/label/hooks/useGetLabelListByIds';
import TemplateLabelListItem from '@app/features/template/components/TemplateLabelInputCard/TemplateLabelListItem';
import { templateLabelIdsChange } from '@app/store/template/actions';

interface TemplateLabelInputCardProps {
  labelIds: string[];
}

function TemplateLabelInputCard({ labelIds }: TemplateLabelInputCardProps) {
  const openModal = useOpenModal();
  const dispatch = useDispatch();
  const { labels, isLoading, isAllLabelAccessible } = useGetLabelListByIds({
    labelIds,
  });

  useEffect(() => {
    if (!isAllLabelAccessible) {
      dispatch(
        templateLabelIdsChange({
          labelIds: labels.map((label) => label.id),
        })
      );
    }
  }, [dispatch, isAllLabelAccessible, labels]);

  const handleLabelDelete = (labelId: string) => {
    dispatch(
      templateLabelIdsChange({
        labelIds: labelIds.filter((lb) => lb !== labelId),
      })
    );
  };

  return (
    <Box backgroundColor="#D2D2D2" borderRadius="3px" mb={4} p={4}>
      <Box backgroundColor="white" borderRadius="3px" boxShadow="0 1px 3px 0 rgba(0,0,0,0.2)" p={6}>
        <Flex flexDirection="column" justifyContent="center">
          <Flex alignItems="center" justifyContent="space-between" sx={{ position: 'relative' }}>
            <Flex alignItems="center">
              <Flex flexDirection="column">
                <Label fontSize={2} mb={0}>
                  요청 문서에 적용할 라벨
                </Label>
                <Text color="textNeutralLight" fontSize="xs" mt={1}>
                  서명 요청 시 문서에 적용할 라벨을 설정할 수 있습니다.
                </Text>
              </Flex>
            </Flex>
            <Button
              height="36px"
              isLoading={isLoading}
              weight="light"
              onClick={() => {
                openModal(({ isOpen, onClose }) => {
                  return (
                    <WindowModal isOpen={isOpen} maxWidth="min-content">
                      <LabelApplyModalPopover
                        excludedHidden
                        initialLabeled={Object.fromEntries(
                          labels.map((label) => [label.id, 'ALL']) ?? []
                        )}
                        mode="limit"
                        showCloseButton="always"
                        onClose={onClose}
                        onSubmit={(labelChanges) => {
                          if (labelChanges.length === 0) {
                            onClose();
                            return;
                          }
                          const toChange = labelChanges
                            .filter((lbc) => lbc.apply)
                            .map((lbc) => lbc.labelId);
                          dispatch(
                            templateLabelIdsChange({
                              labelIds: toChange,
                            })
                          );
                          onClose();
                        }}
                      />
                    </WindowModal>
                  );
                });
              }}
            >
              <FontAwesomeIcon
                icon={faTag}
                style={{
                  color: '#757575',
                  marginRight: '3px',
                }}
              />
              <Text fontWeight={500}>설정</Text>
            </Button>
          </Flex>
          {labels?.length > 0 && (
            <Flex as="ul" flexDirection="column" gap={3} mt={5}>
              {labels.map((label) => {
                if (!label) return null;
                return (
                  <TemplateLabelListItem
                    key={label.id}
                    label={label}
                    onDelete={handleLabelDelete}
                  />
                );
              })}
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
}

export default TemplateLabelInputCard;
